import { useEffect, useState } from 'react';

// #2803 — bright DEV/testing indicator for the PWA, mirroring the Django admin
// app's dev warning (templates/web/base.html). Prod uses RED pervasively (errors,
// destructive actions, and the index.html theme-color #dc2626), so DEV is bright
// YELLOW to be unmistakable.
//
// Environment detection (#2907): derive from the RUNTIME hostname the app is
// actually served from — NOT a build-time var. The previous check keyed off
// import.meta.env.VITE_API_BASE_URL, which Vite inlines at build time; the prod
// build's baked API base didn't contain 'admin.rdmconnect.com', so the DEV
// banner wrongly showed on prod (app.rdmconnect.com). A positive prod allowlist
// is robust to build-config drift and decoupled from the API host: anything not
// exactly a prod host (dev, localhost, previews, unknown) fails SAFE to DEV.
const PROD_HOSTS = ['app.rdmconnect.com'];

export function isDevHost(hostname: string | undefined | null): boolean {
  return !hostname || !PROD_HOSTS.includes(hostname);
}

export const IS_DEV_ENV = typeof window === 'undefined' || isDevHost(window.location.hostname);

const DEV_YELLOW = '#facc15';
const BANNER_HEIGHT = 26; // px
const WARN = '⚠️'; // warning sign emoji (escaped to avoid Windows charmap issues)
const DISMISS_KEY = 'dev_warning_dismissed';

export default function DevEnvBanner() {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!IS_DEV_ENV) return;

    // 1) Recolor the PWA chrome (mobile status bar / installed-app theme) from
    //    the prod red to bright yellow, so DEV is obvious even outside the app.
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute('content') ?? null;
    themeMeta?.setAttribute('content', DEV_YELLOW);

    // 2) Reserve space so the fixed banner never covers app content.
    const prevPad = document.body.style.paddingTop;
    document.body.style.paddingTop = `${BANNER_HEIGHT}px`;

    // 3) Show the one-time-per-session warning modal.
    if (!sessionStorage.getItem(DISMISS_KEY)) setShowModal(true);

    return () => {
      if (prevTheme !== null) themeMeta?.setAttribute('content', prevTheme);
      document.body.style.paddingTop = prevPad;
    };
  }, []);

  if (!IS_DEV_ENV) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setShowModal(false);
  };

  return (
    <>
      <div
        role="status"
        aria-label="Development environment - not real data"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          background: DEV_YELLOW,
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontWeight: 800,
          fontSize: 12,
          letterSpacing: 0.4,
          zIndex: 2147483647,
          borderBottom: '1px solid #a16207',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          // sit above the notch on installed iOS PWAs
          paddingTop: 'env(safe-area-inset-top, 0px)',
          boxSizing: 'content-box',
        }}
      >
        <span aria-hidden>{WARN}</span>
        DEVELOPMENT / TESTING - NOT REAL DATA
        <span aria-hidden>{WARN}</span>
      </div>

      {showModal && (
        <div
          onClick={dismiss}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483646,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: 28,
              maxWidth: 420,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: 44, marginBottom: 8 }} aria-hidden>
              {WARN}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Development Environment</h2>
            <p style={{ color: '#444', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
              This is the <strong>testing app</strong>. Anything you enter here is <strong>not real</strong> and may
              be deleted at any time. If you meant to do real field work, close this and use your normal app link.
            </p>
            <button
              onClick={dismiss}
              style={{
                background: DEV_YELLOW,
                color: '#000',
                border: 'none',
                padding: '11px 30px',
                borderRadius: 9,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              I understand - continue to DEV
            </button>
          </div>
        </div>
      )}
    </>
  );
}
